import { Application, Container } from "pixi.js";
import { ARENA_H, ARENA_W, COLOR } from "./config.ts";

/**
 * Owns the Pixi Application and the arena-to-screen transform. The whole game
 * is authored in a fixed logical arena (ARENA_W x ARENA_H); this class scales
 * and letterboxes that arena onto any screen, and converts pointer coordinates
 * back into arena space for input. Mobile-first: the portrait arena fills the
 * height of a phone and centers on desktop.
 */
export class App {
  readonly pixi = new Application();
  /** Container holding all gameplay, scaled/positioned to fit the screen. */
  readonly arena = new Container();
  /** Container for screen-space UI (HUD/menus), unscaled. */
  readonly ui = new Container();

  private scale = 1;
  private offsetX = 0;
  private offsetY = 0;

  async init(mount: HTMLElement): Promise<void> {
    await this.pixi.init({
      background: COLOR.bg,
      antialias: true,
      resizeTo: mount,
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      autoDensity: true,
      powerPreference: "high-performance",
    });
    mount.appendChild(this.pixi.canvas);
    this.pixi.stage.addChild(this.arena);
    this.pixi.stage.addChild(this.ui);
    this.pixi.renderer.on("resize", () => this.layout());
    this.layout();
  }

  private layout(): void {
    // `renderer.screen` is always in logical (CSS) units, regardless of the
    // device pixel ratio / resolution — the correct basis for letterboxing.
    const w = this.pixi.renderer.screen.width;
    const h = this.pixi.renderer.screen.height;
    this.scale = Math.min(w / ARENA_W, h / ARENA_H);
    this.offsetX = (w - ARENA_W * this.scale) / 2;
    this.offsetY = (h - ARENA_H * this.scale) / 2;
    this.arena.scale.set(this.scale);
    this.arena.position.set(this.offsetX, this.offsetY);
  }

  /** Apply a transient screen-shake offset on top of the letterbox position. */
  applyShake(dx: number, dy: number): void {
    this.arena.position.set(this.offsetX + dx, this.offsetY + dy);
  }

  get screenW(): number {
    return this.pixi.renderer.screen.width;
  }
  get screenH(): number {
    return this.pixi.renderer.screen.height;
  }

  /** Convert client (CSS px) coordinates to arena logical coordinates. */
  mapToArena = (clientX: number, clientY: number, out: { x: number; y: number }): void => {
    const rect = this.pixi.canvas.getBoundingClientRect();
    out.x = (clientX - rect.left - this.offsetX) / this.scale;
    out.y = (clientY - rect.top - this.offsetY) / this.scale;
  };
}
